import {
  collection,
  doc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
  getDoc
} from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { db } from './firebase'
import {
  RescheduleRequest,
  RescheduleRequestDisplay,
  CreateRescheduleResult,
  RespondToRescheduleResult
} from '../types/reschedule'

/**
 * Creates a meeting reschedule request
 *
 * Business rules:
 * - Can only reschedule scheduled meetings
 * - Cannot reschedule within 2 hours of meeting start
 * - Maximum 3 reschedules per meeting
 * - Duration must remain the same
 */
export const createRescheduleRequest = async (
  meetingId: string,
  newStartTime: Timestamp,
  reason?: string
): Promise<CreateRescheduleResult> => {
  try {
    console.log('Creating reschedule request:', { meetingId, newStartTime: newStartTime.toDate().toISOString(), reason })
    const functions = getFunctions()
    const createRescheduleRequestFn = httpsCallable(functions, 'createRescheduleRequest')

    const result = await createRescheduleRequestFn({
      meetingId,
      newStartTime: newStartTime.toDate().toISOString(),
      reason: reason || ''
    })

    console.log('Reschedule request result:', result.data)
    return result.data as CreateRescheduleResult
  } catch (error: any) {
    console.error('Error creating reschedule request:', error)

    // Firebase HttpsError carries .code (e.g. "functions/failed-precondition") and .message with the actual reason
    // Now that the Cloud Function uses HttpsError, the message is the real validation message
    let friendlyMessage = 'Something went wrong while creating the reschedule request. Please try again.'

    if (error?.code && error?.message && error.message !== 'INTERNAL') {
      // HttpsError — use the server's message directly
      friendlyMessage = error.message
    }

    return {
      success: false,
      error: friendlyMessage
    }
  }
}

/**
 * Responds to a reschedule request (approve/reject)
 */
export const respondToRescheduleRequest = async (
  requestId: string,
  approve: boolean,
  message?: string
): Promise<RespondToRescheduleResult> => {
  try {
    const functions = getFunctions()
    const respondToRescheduleFn = httpsCallable(functions, 'respondToRescheduleRequest')

    const result = await respondToRescheduleFn({
      requestId,
      approve,
      message: message || ''
    })

    return result.data as RespondToRescheduleResult
  } catch (error) {
    console.error('Error responding to reschedule request:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to respond to reschedule request'
    }
  }
}

/**
 * Gets pending reschedule requests for a user using user-centric collections
 */
export const getPendingRescheduleRequests = async (
  userId: string
): Promise<RescheduleRequest[]> => {
  try {
    // Query user's reschedule requests collection
    const requestsRef = collection(db, 'userRescheduleRequests', userId, 'requests')
    const q = query(
      requestsRef,
      where('status', '==', 'pending'),
      orderBy('createdAt', 'desc')
    )

    const snapshot = await getDocs(q)
    const requests: RescheduleRequest[] = []

    snapshot.forEach((doc) => {
      requests.push({
        id: doc.id,
        ...doc.data()
      } as RescheduleRequest)
    })

    return requests
  } catch (error) {
    console.error('Error fetching pending reschedule requests:', error)
    return []
  }
}

/**
 * Gets all reschedule requests for a user (pending, approved, rejected)
 */
export const getAllRescheduleRequests = async (
  userId: string,
  limit?: number
): Promise<RescheduleRequest[]> => {
  try {
    const requestsRef = collection(db, 'userRescheduleRequests', userId, 'requests')
    let q = query(requestsRef, orderBy('createdAt', 'desc'))

    if (limit) {
      q = query(q)
    }

    const snapshot = await getDocs(q)
    const requests: RescheduleRequest[] = []

    snapshot.forEach((doc) => {
      requests.push({
        id: doc.id,
        ...doc.data()
      } as RescheduleRequest)
    })

    return requests
  } catch (error) {
    console.error('Error fetching all reschedule requests:', error)
    return []
  }
}

/**
 * Gets a specific reschedule request by ID
 */
export const getRescheduleRequest = async (
  userId: string,
  requestId: string
): Promise<RescheduleRequest | null> => {
  try {
    const requestRef = doc(db, 'userRescheduleRequests', userId, 'requests', requestId)
    const requestDoc = await getDoc(requestRef)

    if (requestDoc.exists()) {
      return {
        id: requestDoc.id,
        ...requestDoc.data()
      } as RescheduleRequest
    }

    return null
  } catch (error) {
    console.error('Error fetching reschedule request:', error)
    return null
  }
}

/**
 * Formats reschedule requests for display
 */
export const formatRescheduleRequestForDisplay = (
  request: RescheduleRequest
): RescheduleRequestDisplay => {
  const now = new Date()
  const expiresAt = request.expiresAt.toDate()
  const isExpired = now >= expiresAt || request.status === 'expired'

  // Calculate time remaining
  let timeRemaining = '0 hours remaining'
  if (!isExpired && request.status === 'pending') {
    const diffMs = expiresAt.getTime() - now.getTime()
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))

    if (diffHours > 0) {
      timeRemaining = `${diffHours} hour${diffHours > 1 ? 's' : ''} remaining`
    } else if (diffMinutes > 0) {
      timeRemaining = `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} remaining`
    } else {
      timeRemaining = 'Expiring soon'
    }
  }

  // Format dates and times
  const originalDate = formatDate(request.originalStartTime.toDate())
  const originalTime = formatTime(request.originalStartTime.toDate())
  const newDate = formatDate(request.newStartTime.toDate())
  const newTime = formatTime(request.newStartTime.toDate())

  return {
    ...request,
    originalDate,
    originalTime,
    newDate,
    newTime,
    timeRemaining,
    isExpired
  }
}

/**
 * Checks if a meeting can be rescheduled
 */
export const canRescheduleMeeting = (
  meeting: {
    status: string
    datetime: string
    rescheduleCount?: number
    rescheduleRequestId?: string
  }
): { canReschedule: boolean; reason?: string } => {
  // Only scheduled meetings can be rescheduled
  if (meeting.status !== 'scheduled') {
    return {
      canReschedule: false,
      reason: 'Only scheduled meetings can be rescheduled'
    }
  }

  // Cannot reschedule within 2 hours of meeting start
  const meetingStart = new Date(meeting.datetime)
  const now = new Date()
  const hoursUntilMeeting = (meetingStart.getTime() - now.getTime()) / (1000 * 60 * 60)

  if (hoursUntilMeeting <= 2) {
    return {
      canReschedule: false,
      reason: 'Cannot reschedule within 2 hours of meeting start'
    }
  }

  // Meeting has already started or passed
  if (meetingStart <= now) {
    return {
      canReschedule: false,
      reason: 'Cannot reschedule meetings that have started'
    }
  }

  // Maximum 3 reschedules per meeting
  const rescheduleCount = meeting.rescheduleCount || 0
  if (rescheduleCount >= 3) {
    return {
      canReschedule: false,
      reason: 'Maximum number of reschedules reached (3)'
    }
  }

  // Check if there's already a pending reschedule request
  if (meeting.rescheduleRequestId) {
    return {
      canReschedule: false,
      reason: 'A reschedule request is already pending'
    }
  }

  return { canReschedule: true }
}

/**
 * Helper: Format date for display
 */
function formatDate(date: Date): string {
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }

  return date.toLocaleDateString('en-US', options)
}

/**
 * Helper: Format time for display
 */
function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  })
}

/**
 * Calculates hours until a meeting starts
 */
export const calculateHoursUntilMeeting = (meetingDate: Date): number => {
  const now = new Date()
  const diffMs = meetingDate.getTime() - now.getTime()
  return diffMs / (1000 * 60 * 60)
}
